import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  Button,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { dashboardService } from '../services/dashboardService';
import { LineChart } from '../components/charts/LineChart';
import { MetricCard } from '../components/dashboard/MetricCard';
import { useWebSocket } from '../services/webSocketService';

const SystemHealth: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [cpuUsage, setCpuUsage] = useState(45.3);
  const [memoryUsage, setMemoryUsage] = useState(62.8);
  const [diskUsage, setDiskUsage] = useState(73.1);
  const [networkThroughput, setNetworkThroughput] = useState(12.6);
  const [gpuUsage, setGpuUsage] = useState(78.5);
  const [workerNodes, setWorkerNodes] = useState(4);
  const [activeNodes, setActiveNodes] = useState(4);
  const [resourceHistory, setResourceHistory] = useState<any>({});
  const theme = useTheme();
  
  // Use WebSocket for real-time resource updates
  const { data: resourceData, isConnected } = useWebSocket<any>('system_resources');
  
  useEffect(() => {
    fetchSystemResources();
    
    // Set up polling for resource data
    const interval = setInterval(() => {
      fetchSystemResources();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    if (resourceData) {
      setCpuUsage(resourceData.cpu_usage || cpuUsage);
      setMemoryUsage(resourceData.memory_usage || memoryUsage);
      setDiskUsage(resourceData.disk_usage || diskUsage);
      setNetworkThroughput(resourceData.network_throughput || networkThroughput);
      setGpuUsage(resourceData.gpu_usage || gpuUsage);
      setWorkerNodes(resourceData.worker_nodes || workerNodes);
      setActiveNodes(resourceData.active_nodes || activeNodes);
      setLastUpdated(new Date());
    }
  }, [resourceData]);
  
  const fetchSystemResources = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const resources = await dashboardService.getSystemResources();
      
      setCpuUsage(resources.cpu_usage);
      setMemoryUsage(resources.memory_usage);
      setDiskUsage(resources.disk_usage);
      setNetworkThroughput(resources.network_throughput);
      setGpuUsage(resources.gpu_usage);
      setWorkerNodes(resources.worker_nodes);
      setActiveNodes(resources.active_nodes);
      
      // Fetch telemetry metrics for historical data
      const telemetry = await dashboardService.getTelemetryMetrics();
      setResourceHistory(telemetry.metrics);
      
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to fetch system resources');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // CPU usage history chart data
  const cpuHistoryData = {
    labels: ['12:00', '12:05', '12:10', '12:15', '12:20', '12:25', '12:30'],
    datasets: [
      {
        label: 'CPU Usage (%)',
        data: resourceHistory.cpu_usage || [42, 45, 43, 47, 46, 44, cpuUsage],
        borderColor: theme.palette.primary.main,
        fill: true,
      }
    ]
  };
  
  // Memory usage history chart data
  const memoryHistoryData = {
    labels: ['12:00', '12:05', '12:10', '12:15', '12:20', '12:25', '12:30'],
    datasets: [
      {
        label: 'Memory Usage (%)',
        data: resourceHistory.memory_usage || [58, 62, 65, 63, 61, 64, memoryUsage],
        borderColor: theme.palette.secondary.main,
        fill: true,
      }
    ]
  };
  
  // GPU usage history chart data (if available)
  const gpuHistoryData = {
    labels: ['12:00', '12:05', '12:10', '12:15', '12:20', '12:25', '12:30'],
    datasets: [
      {
        label: 'GPU Usage (%)',
        data: [65, 72, 75, 78, 74, 76, gpuUsage],
        borderColor: theme.palette.error.main,
        fill: true,
      }
    ]
  };
  
  // Network throughput history chart data
  const networkHistoryData = {
    labels: ['12:00', '12:05', '12:10', '12:15', '12:20', '12:25', '12:30'],
    datasets: [
      {
        label: 'Network Traffic (MB/s)',
        data: [10.2, 11.5, 13.2, 12.1, 11.8, 12.4, networkThroughput],
        borderColor: theme.palette.info.main,
        fill: true,
      }
    ]
  };
  
  // Mock worker node data for the table
  const workerNodeData = [
    { id: 'worker-1', name: 'Prediction Worker 1', status: 'active', cpu: 65.2, memory: 72.5, load: 'normal' },
    { id: 'worker-2', name: 'Prediction Worker 2', status: 'active', cpu: 82.1, memory: 78.3, load: 'high' },
    { id: 'worker-3', name: 'Data Processing Node', status: 'active', cpu: 45.7, memory: 52.2, load: 'normal' },
    { id: 'worker-4', name: 'Training Node', status: 'active', cpu: 95.3, memory: 88.1, load: 'critical' },
  ];
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h5" component="h1">
          System Health Monitoring
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Chip 
            label={isConnected ? 'Real-time: Active' : 'Real-time: Inactive'} 
            color={isConnected ? 'success' : 'error'} 
            size="small" 
            sx={{ mr: 2 }} 
          />
          
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
          
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={fetchSystemResources}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {/* Resource Usage Metrics */}
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard 
            title="CPU Usage"
            value={cpuUsage}
            unit="%"
            status={cpuUsage > 80 ? 'error' : cpuUsage > 60 ? 'warning' : 'success'}
            icon="cpu"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard 
            title="Memory Usage"
            value={memoryUsage}
            unit="%"
            status={memoryUsage > 80 ? 'error' : memoryUsage > 60 ? 'warning' : 'success'}
            icon="memory"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard 
            title="Disk Usage"
            value={diskUsage}
            unit="%"
            status={diskUsage > 85 ? 'error' : diskUsage > 70 ? 'warning' : 'success'}
            icon="disk"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard 
            title="GPU Usage"
            value={gpuUsage}
            unit="%"
            status={gpuUsage > 85 ? 'error' : gpuUsage > 70 ? 'warning' : 'success'}
            icon="custom"
            customIcon={<RefreshIcon fontSize="large" />}
          />
        </Grid>
        
        {/* Network and Worker Metrics */}
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard 
            title="Network Throughput"
            value={networkThroughput}
            unit="MB/s"
            status="info"
            icon="network"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard 
            title="Worker Nodes"
            value={workerNodes}
            unit="nodes"
            status={activeNodes < workerNodes ? 'warning' : 'success'}
            icon="users"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard 
            title="Active Nodes"
            value={activeNodes}
            unit="nodes"
            status={activeNodes < workerNodes ? 'warning' : 'success'}
            icon="users"
          />
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <MetricCard 
            title="Node Health"
            value={(activeNodes / workerNodes) * 100}
            unit="%"
            status={activeNodes < workerNodes * 0.8 ? 'error' : activeNodes < workerNodes ? 'warning' : 'success'}
            icon="custom"
            customIcon={<RefreshIcon fontSize="large" />}
          />
        </Grid>
        
        {/* CPU Usage Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>CPU Usage History</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 300 }}>
              <LineChart 
                labels={cpuHistoryData.labels}
                datasets={cpuHistoryData.datasets}
                height={300}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Memory Usage Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Memory Usage History</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 300 }}>
              <LineChart 
                labels={memoryHistoryData.labels}
                datasets={memoryHistoryData.datasets}
                height={300}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* GPU Usage Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>GPU Usage History</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 300 }}>
              <LineChart 
                labels={gpuHistoryData.labels}
                datasets={gpuHistoryData.datasets}
                height={300}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Network Usage Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Network Traffic History</Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ height: 300 }}>
              <LineChart 
                labels={networkHistoryData.labels}
                datasets={networkHistoryData.datasets}
                height={300}
              />
            </Box>
          </Paper>
        </Grid>
        
        {/* Worker Nodes Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Worker Node Status</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Node ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>CPU Usage</TableCell>
                    <TableCell>Memory Usage</TableCell>
                    <TableCell>Load</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {workerNodeData.map((node) => (
                    <TableRow key={node.id}>
                      <TableCell>{node.id}</TableCell>
                      <TableCell>{node.name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={node.status} 
                          color={node.status === 'active' ? 'success' : 'error'} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={node.cpu} 
                              color={node.cpu > 80 ? 'error' : node.cpu > 60 ? 'warning' : 'success'}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Box sx={{ minWidth: 35 }}>
                            <Typography variant="body2" color="text.secondary">{`${node.cpu}%`}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={node.memory} 
                              color={node.memory > 80 ? 'error' : node.memory > 60 ? 'warning' : 'success'}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Box sx={{ minWidth: 35 }}>
                            <Typography variant="body2" color="text.secondary">{`${node.memory}%`}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={node.load} 
                          color={
                            node.load === 'critical' 
                              ? 'error' 
                              : node.load === 'high' 
                                ? 'warning' 
                                : 'success'
                          } 
                          size="small" 
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemHealth;
